import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { API, useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useUploadManager } from "@/context/UploadManagerContext";
import {
  Upload, Download, Trash2, CheckCircle2, Folder, FolderOpen, Lock, FileText,
  RotateCcw, XCircle, Wand2, Eye, UserCog, Users, RefreshCw, Loader2,
  ChevronDown, Package,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import AppTopBar from "@/components/AppTopBar";
import FileTreeView from "@/components/FileTreeView";
import FolderViewer from "@/components/FolderViewer";
import SessionDetails from "@/components/SessionDetails";

function StatusPill({ status }) {
  const cls =
    status === "ready_for_review"
      ? "bg-orange-50 text-orange-700 border-orange-200"
      : status === "closed"
      ? "bg-slate-100 text-slate-700 border-slate-300"
      : "bg-blue-50 text-blue-700 border-blue-200";
  const label =
    status === "ready_for_review" ? "Ready for review"
    : status === "closed" ? "Closed" : "In progress";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold tracking-wide border ${cls}`}>
      {label}
    </span>
  );
}

function bytesToHuman(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function FolderPanel({ jobId, folder, canUpload, canDelete, tone, jobName, onChanged, onAnalyzeFolder, testidPrefix }) {
  const [files, setFiles] = useState([]);
  const [totalBytes, setTotalBytes] = useState(0);
  const [subfolderStats, setSubfolderStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [zipping, setZipping] = useState(false);
  // Path of the folder currently open in the viewer modal, or null when
  // the viewer is closed. Empty string ("") opens the ENTIRE folder
  // (root of Uploaded / Edited).
  const [viewerPath, setViewerPath] = useState(null);
  const inputRef = useRef();
  const folderInputRef = useRef();

  const { startUpload, cancelUpload, getUpload } = useUploadManager();
  const upload = getUpload(jobId, folder);
  const uploading = !!upload;
  const progress = upload?.progressPct ?? 0;
  const phase = upload?.phase || "idle";
  const uploadCount = upload?.fileCount || 0;
  const uploadDoneCount = upload?.done || 0;
  const uploadFailedCount = upload?.failed || 0;
  const currentFileName = uploadCount === 1
    ? "file"
    : `${uploadCount} files`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}/files`, { params: { folder } });
      setFiles(data.files || []);
      setTotalBytes(data.total_bytes || 0);
      setSubfolderStats(data.subfolders || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [jobId, folder]);

  useEffect(() => { load(); }, [load]);

  // When the global upload for this (job, folder) finishes, refresh
  // the file list so newly-uploaded rows appear. NOTE: getUpload()
  // filters out uploads that have already flipped to phase="done"
  // (they're considered "settled"), so we never observe a
  // phase === "done" tick here. Instead we watch the transition
  // where an ACTIVE upload → disappears entirely (upload becomes
  // undefined) which is the moment the UploadManager marks it done
  // or cancelled. That's when we refresh.
  const wasUploading = useRef(false);
  useEffect(() => {
    if (wasUploading.current && !uploading) {
      // Upload just finished (either cleanly or via cancel).
      load();
      onChanged && onChanged();
    }
    wasUploading.current = uploading;
  }, [uploading, load, onChanged]);

  const handleUpload = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (uploading) {
      toast.warning("An upload for this folder is already running — cancel it first if you want to start a new one.");
      return;
    }
    startUpload(jobId, folder, fileList, { jobName });
    // Reset the input so re-selecting the same folder still fires onChange.
    if (inputRef.current) inputRef.current.value = "";
    if (folderInputRef.current) folderInputRef.current.value = "";
  };

  const handleCancelUpload = () => {
    if (upload) {
      cancelUpload(upload.id);
      toast("Cancelling upload…");
    }
  };

  const handleDownload = async (name) => {
    try {
      const url = `${API}/jobs/${jobId}/files/${encodeURIComponent(name)}/download?folder=${folder}`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    }
  };

  const handleDownloadAll = async () => {
    if (files.length === 0) return;
    setZipping(true);
    try {
      // ── Pre-flight ──────────────────────────────────────────────
      // Do a HEAD-ish poke so any 401 kicks the axios interceptor to
      // refresh the access-token cookie BEFORE the browser starts
      // the streaming download. Long downloads (30+ min for 5 GB)
      // used to fail mid-flight when the token expired 15 min in.
      await axios.get(`${API}/auth/me`);

      // ── Browser-native streaming download ───────────────────────
      // `<a href download>` streams directly from the response to
      // disk with **no in-memory buffering** — critical for 3-5 GB
      // archives that used to blow up r.blob(). The browser also
      // handles content-length, progress, cancel, and retry natively.
      const url = `${API}/jobs/${jobId}/files/zip?folder=${folder}`;
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      // `download` attribute triggers Save-As dialog on same-origin
      // and streams via the browser's native download manager.
      a.download = `${folder}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.info(
        "Download started — check your browser's download bar. Large archives can take a while to stream.",
        { duration: 8000 },
      );
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally {
      // Flip back quickly — the actual streaming happens in the
      // browser's download manager, not in this React app.
      setTimeout(() => setZipping(false), 1500);
    }
  };

  // ── Drag-and-drop upload (files or entire folders) ────────────────
  // Uses the DataTransferItem.webkitGetAsEntry() API which is available
  // in Chromium and Safari. Falls back to the flat list of files for
  // browsers that don't expose the entry API.
  const collectDroppedFiles = async (dataTransfer) => {
    const items = dataTransfer.items;
    if (!items || items.length === 0) {
      return Array.from(dataTransfer.files || []);
    }
    const roots = [];
    for (const it of items) {
      if (typeof it.webkitGetAsEntry !== "function") {
        const f = it.getAsFile && it.getAsFile();
        if (f) roots.push({ entry: null, file: f });
      } else {
        const entry = it.webkitGetAsEntry();
        if (entry) roots.push({ entry, file: null });
      }
    }
    const out = [];
    const walk = async (entry, prefix) => {
      if (entry.isFile) {
        await new Promise((res, rej) => {
          entry.file((f) => {
            // Attach webkitRelativePath so the uploader picks it up
            try {
              Object.defineProperty(f, "webkitRelativePath", {
                value: prefix ? `${prefix}/${f.name}` : f.name,
              });
            } catch {
              /* some browsers block this; upload falls back to file.name */
            }
            out.push(f);
            res();
          }, rej);
        });
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        // readEntries only returns 100 at a time — loop until empty.
        let batch = [];
        do {
          batch = await new Promise((res, rej) =>
            reader.readEntries(res, rej));
          for (const child of batch) {
            await walk(child, prefix ? `${prefix}/${entry.name}` : entry.name);
          }
        } while (batch.length > 0);
      }
    };
    for (const r of roots) {
      if (r.file) out.push(r.file);
      else if (r.entry) await walk(r.entry, "");
    }
    return out;
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canUpload || uploading) return;
    try {
      const files = await collectDroppedFiles(e.dataTransfer);
      if (files.length > 0) handleUpload(files);
    } catch (err) {
      toast.error(`Drag-drop failed: ${err.message || err}`);
    }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}/files/${encodeURIComponent(name)}`, {
        params: { folder },
      });
      toast.success("Deleted");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const accentDot = tone === "primary" ? "bg-primary" : "bg-accent";

  return (
    <section
      className={`relative bg-white border rounded-md p-6 flex flex-col transition-colors ${
        dragOver && canUpload && !uploading
          ? "border-primary ring-2 ring-primary/30 bg-primary/5"
          : "border-border"
      }`}
      data-testid={`${testidPrefix}-panel`}
      onDragOver={(e) => {
        if (!canUpload || uploading) return;
        e.preventDefault();
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={(e) => {
        // Only unset when the pointer really leaves the section
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDragOver(false);
      }}
      onDrop={handleDrop}
    >
      <style>{`
        @keyframes r2-saving-slide {
          0%   { left: -35%; }
          100% { left: 100%; }
        }
      `}</style>
      <header className="flex items-start justify-between mb-5 gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-2">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${accentDot}`} />
            {folder === "uploaded" ? "Source" : "Deliverables"}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <Folder className="h-4 w-4 text-slate-400" />
            <h2 className="font-heading text-xl font-bold capitalize tracking-tight">
              {folder}
            </h2>
            <span className="text-sm text-slate-500 font-semibold tabular-nums" data-testid={`${testidPrefix}-folder-count`}>
              {files.length}
              {totalBytes > 0 && (
                <span className="ml-2 text-slate-400 font-medium">
                  · {bytesToHuman(totalBytes)}
                </span>
              )}
            </span>
            {files.length > 0 && (
              <Button
                size="sm" variant="ghost"
                onClick={() => setViewerPath("")}
                className="h-7 px-2 text-[11px] font-semibold text-slate-600 hover:bg-slate-100 rounded-md"
                data-testid={`${testidPrefix}-view-all`}
                title="Browse every file in this folder"
              >
                <Eye className="h-3.5 w-3.5 mr-1" />
                View
              </Button>
            )}
            {!canDelete && (
              <span title="Delete disabled by policy" className="text-xs text-slate-400 inline-flex items-center gap-1">
                <Lock className="h-3 w-3" />no delete
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {files.length > 0 && (
            <Button
              size="sm" variant="outline"
              onClick={handleDownloadAll}
              disabled={zipping}
              className="border-border text-slate-700 hover:bg-slate-50 rounded-md h-9"
              data-testid={`${testidPrefix}-download-all`}
            >
              <Download className="h-4 w-4 mr-1.5" />
              {zipping ? "Zipping…" : "Download all"}
            </Button>
          )}
          {canUpload && (
            <>
              <input
                ref={inputRef} type="file" multiple
                className="hidden" style={{ display: "none" }}
                onChange={(e) => handleUpload(e.target.files)}
                data-testid={`${testidPrefix}-file-input`}
              />
              <input
                ref={folderInputRef} type="file" multiple
                className="hidden" style={{ display: "none" }}
                webkitdirectory=""
                onChange={(e) => handleUpload(e.target.files)}
                data-testid={`${testidPrefix}-folder-input`}
              />
              <Button
                size="sm" variant="outline"
                onClick={() => folderInputRef.current?.click()}
                disabled={uploading}
                className="border-border text-slate-700 hover:bg-slate-50 rounded-md h-9 font-semibold"
                title="Upload an entire folder — keeps its subfolder structure"
                data-testid={`${testidPrefix}-folder-btn`}
              >
                <Folder className="h-4 w-4 mr-1.5" />
                Upload folder
              </Button>
              <Button
                size="sm"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-md h-9 font-semibold shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow]"
                data-testid={`${testidPrefix}-upload-btn`}
              >
                <Upload className="h-4 w-4 mr-1.5" />
                {!uploading
                  ? "Upload files"
                  : phase === "saving"
                  ? "Saving to storage…"
                  : phase === "retrying"
                  ? `Retrying failed…`
                  : `Uploading ${progress}%`}
              </Button>
            </>
          )}
        </div>
      </header>

      {uploading && (
        <div className="mb-4" data-testid={`${testidPrefix}-progress`}>
          <div className="flex items-center justify-between mb-1 gap-3">
            <p className="text-xs font-semibold text-slate-600 truncate">
              {phase === "saving"
                ? `Finalising on Cloudflare R2…`
                : phase === "retrying"
                  ? `Retrying failed uploads — ${uploadFailedCount} left…`
                  : uploadCount > 1
                    ? `Uploading ${uploadDoneCount}/${uploadCount} files${uploadFailedCount ? ` — ${uploadFailedCount} failed so far` : ""}`
                    : `Uploading ${currentFileName || "file"}…`}
            </p>
            <div className="flex items-center gap-3 shrink-0">
              <p className="text-xs font-mono tabular-nums text-slate-500">
                {phase === "saving" ? "please wait" : `${progress}%`}
              </p>
              <button
                type="button"
                onClick={handleCancelUpload}
                className="text-xs font-semibold text-slate-500 hover:text-red-600 hover:underline"
                data-testid={`${testidPrefix}-upload-cancel`}
              >
                Cancel
              </button>
            </div>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden relative">
            {phase === "saving" ? (
              <div
                className="absolute inset-y-0 w-1/3 bg-primary rounded-full"
                style={{ animation: "r2-saving-slide 1.1s ease-in-out infinite" }}
              />
            ) : (
              <div
                className="h-full bg-primary transition-[width] duration-150"
                style={{ width: `${progress}%` }}
              />
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2 flex-1">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-9 bg-slate-100 animate-pulse rounded-sm" />
          ))}
        </div>
      ) : files.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-border rounded-md p-8 min-h-[180px] text-center">
          <FolderOpen className="h-8 w-8 text-slate-300 mb-3" />
          <p className="font-semibold text-slate-700">Empty folder.</p>
          {canUpload && (
            <p className="text-sm text-slate-500 mt-1 font-medium">
              Drag files or a folder here — or click <b>Upload files</b> / <b>Upload folder</b>.
            </p>
          )}
        </div>
      ) : (
        <FileTreeView
          files={files}
          subfolderStats={subfolderStats}
          canDelete={canDelete}
          testidPrefix={testidPrefix}
          onDownload={handleDownload}
          onDelete={handleDelete}
          onAnalyzeFolder={onAnalyzeFolder}
          onView={(path) => setViewerPath(path)}
        />
      )}
      {dragOver && canUpload && !uploading && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center rounded-md">
          <div className="bg-primary/10 border-2 border-primary border-dashed rounded-md px-6 py-3 text-primary font-bold text-sm shadow-sm">
            Drop to upload
          </div>
        </div>
      )}
      {viewerPath !== null && (
        <FolderViewer
          jobId={jobId}
          folder={folder}
          path={viewerPath}
          onClose={() => setViewerPath(null)}
          onAnalyze={onAnalyzeFolder}
        />
      )}
    </section>
  );
}

/* ── RecentAnalyses ─────────────────────────────────────────────────
 * Compact horizontal list of every analyzer session ever kicked off
 * for this job. Each card shows a live status pill (downloading /
 * analyzing / ready / failed), progress counts, the folder scope,
 * and a click-through into that session. Polled by the parent every
 * few seconds while any session is still active.
 */
function RecentAnalyses({ sessions, onOpen, jobId, isAdmin, onCancelled }) {
  const [cancellingId, setCancellingId] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  const [resumingId, setResumingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  // Phase 81: track which session (if any) the user has just fired an
  // AI-regroup on — until the /sessions poll returns
  // ``ai_grouping_progress.status === "running"`` we optimistically
  // render the "Starting…" state to give instant feedback.
  const [startingRegroupId, setStartingRegroupId] = useState(null);

  // Phase 67: when the admin analyzes a folder with more files than
  // ``batch_size``, the backend splits it into N sibling sessions and
  // links batches 2..N to batch 1 via ``parent_session_id`` +
  // ``batch_sibling=true``. From the admin's point of view they are
  // ONE analysis — so we collapse siblings into the parent card. The
  // parent's ``photo_count`` / groups already merge every batch via
  // the retry-child merge in ``GET /api/sessions/{sid}``.
  const visibleSessions = sessions.filter(
    (s) => !(s.batch_sibling && s.parent_session_id),
  );
  // Re-render every 30s so the "Stalled X min ago" tooltip stays fresh
  // even when no new sessions poll response has come back yet.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const STALL_WARN_MS = 3 * 60 * 1000; // 3 min without progress → visual warning

  const isStalled = (s) => {
    if (s.status !== "downloading" && s.status !== "analyzing") return false;
    const stamp = s.progress_updated_at || s.created_at;
    if (!stamp) return false;
    const t = new Date(stamp).getTime();
    if (Number.isNaN(t)) return false;
    return Date.now() - t > STALL_WARN_MS;
  };

  const stalledMinutes = (s) => {
    const stamp = s.progress_updated_at || s.created_at;
    if (!stamp) return 0;
    const t = new Date(stamp).getTime();
    if (Number.isNaN(t)) return 0;
    return Math.floor((Date.now() - t) / 60_000);
  };

  const cancel = async (e, s) => {
    e.stopPropagation();
    const active = s.status === "downloading" || s.status === "analyzing";
    const isDone = s.status === "complete" || s.status === "ready";
    let confirmMsg;
    if (active) {
      confirmMsg =
        "Cancel this analysis and delete all its cached files?\n\n" +
        "Downloaded originals, RAW previews, and any partial analysis " +
        "for this session will be permanently removed. Uploaded files " +
        "in R2 are NOT affected — you can restart the analysis anytime.";
    } else if (isDone) {
      confirmMsg =
        "Delete this analysis?\n\n" +
        "Its downloaded originals, previews, and analysis results " +
        "(groups, scores, markups on the analyzer) will be removed. " +
        "Uploaded files in R2 are NOT affected.";
    } else {
      confirmMsg =
        "Delete this failed/cancelled session and its cache?\n\n" +
        "Uploaded files in R2 are NOT affected.";
    }
    if (!window.confirm(confirmMsg)) return;
    setCancellingId(s.id);
    try {
      const { data } = await axios.delete(
        `${API}/jobs/${jobId}/analyze/sessions/${s.id}`,
      );
      const freedMb = ((data?.freed_bytes || 0) / (1024 * 1024)).toFixed(1);
      toast.success(
        Number(freedMb) > 0
          ? `${active ? "Cancelled" : "Deleted"} — freed ${freedMb} MB of cache`
          : `${active ? "Cancelled" : "Deleted"} — cache cleared`,
      );
      onCancelled && onCancelled();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || err.message,
      );
    } finally {
      setCancellingId(null);
    }
  };

  // Phase 55/60: retry the errored files with an EXTENDED per-file
  // timeout so slow-decoding RAWs (typical culprit: 40+ MP sensors +
  // cold R2 cache) get a fair second chance. Prompts once to pick
  // 1× / 3× / 6× — most cases the 3× default lands them all.
  const retryErrors = async (e, s) => {
    e.stopPropagation();
    if (!s.error_count || s.error_count <= 0) return;
    const promptMsg =
      `Retry the ${s.error_count} errored file${s.error_count === 1 ? "" : "s"} `
      + `from this session with an extended timeout.\n\n`
      + `Timeout multiplier (default 3× = 270 s per file):\n`
      + `  • 1  → 90 s (original)\n`
      + `  • 3  → 270 s (default — handles most slow-decode RAWs)\n`
      + `  • 6  → 540 s (last-resort for very large sensors on cold cache)`;
    const raw = window.prompt(promptMsg, "3");
    if (raw == null) return;  // cancelled
    const mult = Math.max(1, Math.min(6, parseFloat(raw) || 3));
    setRetryingId(s.id);
    try {
      const { data } = await axios.post(
        `${API}/jobs/${jobId}/analyze/sessions/${s.id}/retry-errors`,
        null,
        { params: { timeout_multiplier: mult } },
      );
      toast.success(
        `Retrying ${data?.retried_count ?? 0} file${(data?.retried_count ?? 0) === 1 ? "" : "s"} `
        + `at ${data?.per_file_timeout_sec ?? "extended"} s/file`,
      );
      onCancelled && onCancelled();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || err.message,
      );
    } finally {
      setRetryingId(null);
    }
  };

  // Phase 66: resume a stalled / active session — cancels the source
  // (if still running) then kicks a child session that only processes
  // files which haven't finished. Completed work is preserved via the
  // parent-child session merge.
  const resumeSession = async (e, s) => {
    e.stopPropagation();
    const total = s.download_total ?? 0;
    const done = s.downloaded_count ?? 0;
    const remaining = Math.max(0, total - done);
    const confirmMsg =
      `Resume this session?\n\n`
      + `• ${done} file${done === 1 ? "" : "s"} already analyzed — kept.\n`
      + `• ~${remaining} unfinished file${remaining === 1 ? "" : "s"} will restart.\n`
      + `• Any files still running here will be cancelled.\n\n`
      + `Continue?`;
    if (!window.confirm(confirmMsg)) return;
    setResumingId(s.id);
    try {
      const { data } = await axios.post(
        `${API}/jobs/${jobId}/analyze/sessions/${s.id}/resume`,
      );
      toast.success(
        `Resumed — ${data?.resumed_count ?? 0} unfinished file${(data?.resumed_count ?? 0) === 1 ? "" : "s"} restarting, `
        + `${data?.already_done_count ?? 0} kept`,
      );
      onCancelled && onCancelled();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || err.message,
      );
    } finally {
      setResumingId(null);
    }
  };

  // Phase 81: kick AI grouping right from the RecentAnalyses card so
  // the admin never has to leave the Job page. The endpoint is
  // fire-and-forget (202) — the parent poll picks up progress via
  // ``s.ai_grouping_progress`` heartbeats and renders live status.
  const startAiRegroup = async (e, s, provider) => {
    e.preventDefault();
    e.stopPropagation();
    setStartingRegroupId(s.id);
    try {
      await axios.post(
        `${API}/sessions/${s.id}/ai-regroup`,
        null,
        { params: { provider } },
      );
      toast.success(`Grouping ${s.photo_count ?? "?"} photos with ${provider === "gemini" ? "Gemini" : "OpenAI"} — runs in the background`);
      // Nudge the parent to refresh so the running state shows up in
      // the next 1-2 s instead of waiting for the 3 s poll tick.
      onCancelled && onCancelled();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || err.message,
      );
      setStartingRegroupId(null);
    }
    // Note: we keep ``startingRegroupId`` set until the poll returns
    // ``running`` (or ``complete``/``failed``) — the card's inline
    // logic swaps to the poll's progress the moment it arrives.
  };

  const pillFor = (s) => {
    if (isStalled(s)) {
      return (
        <span
          className="inline-flex items-center rounded-full px-2 h-5 text-[10px] font-bold uppercase tracking-widest border bg-red-50 text-red-700 border-red-300 animate-pulse gap-1"
          data-testid={`analysis-pill-stalled-${s.id}`}
          title={`No progress for ${stalledMinutes(s)} min — auto-cancel at 10 min`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
          stalled · {stalledMinutes(s)}m
        </span>
      );
    }
    const status = s.status || "ready";
    // Show a distinct "PREPARING" label whenever workers are stalled
    // on background preview generation — the "downloading" pill would
    // be misleading (nothing is actually downloading; we're waiting
    // on rawpy in the bg preview pool).
    const displayStatus =
      status === "downloading" && (s.preparing_previews_count ?? 0) > 0
        ? "preparing"
        : status;
    const styles = {
      queued: "bg-slate-100 text-slate-700 border-slate-300",
      downloading: "bg-blue-50 text-blue-700 border-blue-200",
      preparing: "bg-violet-50 text-violet-700 border-violet-200",
      analyzing: "bg-amber-50 text-amber-700 border-amber-200",
      ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
      complete: "bg-emerald-50 text-emerald-700 border-emerald-200",
      failed: "bg-red-50 text-red-700 border-red-200",
      cancelled: "bg-slate-100 text-slate-500 border-slate-200",
      error: "bg-red-50 text-red-700 border-red-200",
    };
    const cls = styles[displayStatus] || "bg-slate-50 text-slate-700 border-slate-200";
    return (
      <span className={`inline-flex items-center rounded-full px-2 h-5 text-[10px] font-bold uppercase tracking-widest border ${cls}`}>
        {displayStatus}
      </span>
    );
  };

  const progressText = (s) => {
    // "Preparing preview files…" trumps the raw download counter
    // whenever workers are blocked waiting on background preview
    // generation. Per user request (Phase 40): analysis never falls
    // back to the CR2 slow-path, so a stalled download count usually
    // means we're waiting on JPEG previews to be baked.
    if (s.status === "downloading" && (s.preparing_previews_count ?? 0) > 0) {
      const n = s.preparing_previews_count;
      const done = s.downloaded_count ?? 0;
      const total = s.download_total ?? 0;
      return `Preparing ${n} preview file${n === 1 ? "" : "s"}… (${done} / ${total} ready)`;
    }
    if (s.status === "downloading" && s.download_total > 0) {
      // Phase 54: show batch progress on large sessions so the user
      // sees "Batch 3/8" while a 700-photo folder is chewing through
      // its waves — reassurance that the analyzer hasn't stalled.
      const batchTag = (s.total_batches && s.total_batches > 1)
        ? ` · Batch ${s.current_batch ?? 0}/${s.total_batches}`
        : "";
      const retryTag = (s.retry_attempt && s.retry_attempted_count)
        ? ` · retrying ${s.retry_attempted_count}`
        : "";
      return `${s.downloaded_count ?? 0} / ${s.download_total} downloading…${batchTag}${retryTag}`;
    }
    if (s.status === "analyzing" && s.photo_count > 0) {
      const batchTag = (s.total_batches && s.total_batches > 1)
        ? ` · Batch ${s.current_batch ?? 0}/${s.total_batches}`
        : "";
      const retryTag = (s.retry_attempt && s.retry_attempted_count)
        ? ` · retrying ${s.retry_attempted_count}`
        : "";
      return `${s.analyzed_count ?? 0} / ${s.photo_count} analyzed…${batchTag}${retryTag}`;
    }
    if (s.status === "ready" || s.status === "complete") {
      return `${s.photo_count ?? 0} photos · ${s.group_count ?? 0} groups`;
    }
    if (s.status === "queued") {
      const total = s.download_total ?? 0;
      return total > 0
        ? `Queued — ${total} file${total === 1 ? "" : "s"} waiting for a free slot…`
        : "Queued — waiting for a free slot…";
    }
    if (s.status === "failed" || s.status === "error") return "Failed";
    if (s.status === "cancelled") return "Cancelled";
    return `${s.photo_count ?? 0} photos`;
  };

  // Human-friendly session timing/throughput line shown below the
  // progress text. Renders different info depending on status:
  //   • active      → "Started 3 min ago · 1.2 files/s"
  //   • complete    → "Finished in 2m 15s · 1.06 files/s"
  //   • failed      → "Failed after 45s"
  //   • terminal    → nothing (falls back to created_at above)
  const formatSecs = (secs) => {
    if (secs == null) return "";
    const n = Math.round(secs);
    if (n < 60) return `${n}s`;
    if (n < 3600) return `${Math.floor(n / 60)}m ${n % 60}s`;
    return `${Math.floor(n / 3600)}h ${Math.floor((n % 3600) / 60)}m`;
  };
  const relSecs = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 1000 : null);
  const timingLine = (s) => {
    const status = s.status;
    if (status === "downloading" || status === "analyzing") {
      const started = relSecs(s.started_at || s.created_at);
      if (!started) return "";
      // Live throughput: files processed / seconds elapsed.
      const processed = (s.downloaded_count ?? 0) + (status === "analyzing" ? (s.analyzed_count ?? 0) : 0);
      const rate = started > 3 ? (processed / started).toFixed(2) : null;
      return rate ? `Running ${formatSecs(started)} · ${rate} files/s` : `Running ${formatSecs(started)}`;
    }
    if (status === "complete") {
      const dur = s.duration_seconds;
      const fps = s.files_per_second;
      if (dur != null && fps != null) return `Finished in ${formatSecs(dur)} · ${fps} files/s`;
      if (dur != null) return `Finished in ${formatSecs(dur)}`;
      return "";
    }
    if ((status === "failed" || status === "error") && s.duration_seconds != null) {
      return `Failed after ${formatSecs(s.duration_seconds)}`;
    }
    return "";
  };

  const cancelTitle = (s) => {
    if (s.status === "downloading" || s.status === "analyzing")
      return "Cancel this analysis and delete its cache";
    if (s.status === "complete" || s.status === "ready")
      return "Delete this analysis and its cache";
    return "Delete this session and its cache";
  };

  const [clearingAll, setClearingAll] = useState(false);
  const clearAll = async () => {
    if (!isAdmin) return;
    if (!window.confirm(
      `Clear ALL ${sessions.length} analyses on this job?\n\n`
      + "Every session (downloading, complete, failed) and every "
      + "analyzed photo will be permanently removed so you can start "
      + "fresh. Uploaded files in R2 are NOT touched.",
    )) return;
    setClearingAll(true);
    try {
      const { data } = await axios.delete(
        `${API}/jobs/${jobId}/analyze/sessions`,
      );
      const mb = ((data?.freed_bytes || 0) / (1024 * 1024)).toFixed(1);
      toast.success(
        `Cleared ${data.session_count} session${data.session_count === 1 ? "" : "s"}`
        + (data.photo_count ? ` · ${data.photo_count} photos` : "")
        + (Number(mb) > 0 ? ` · freed ${mb} MB` : ""),
      );
      onCancelled && onCancelled();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || err.message,
      );
    } finally {
      setClearingAll(false);
    }
  };

  return (
    <section
      className="bg-white border border-border rounded-md p-5"
      data-testid="recent-analyses"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">
          Recent analyses
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-slate-500 font-semibold" data-testid="recent-analyses-count">
            {visibleSessions.length} total
          </span>
          {isAdmin && sessions.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              disabled={clearingAll}
              className="text-[11px] font-semibold text-red-600 hover:text-red-700 hover:underline disabled:opacity-50 disabled:cursor-wait"
              data-testid="analyses-clear-all"
              title="Delete every analyzer session for this job (starts fresh)"
            >
              {clearingAll ? "Clearing…" : "Clear all"}
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {visibleSessions.map((s) => {
          // Admin can delete every session — active ones get cancelled,
          // terminal ones (complete/failed/cancelled) are just purged.
          const canCancel = isAdmin;
          const stalled = isStalled(s);
          return (
            <div
              key={s.id}
              className={`relative border rounded-md hover:border-primary/40 transition-colors min-w-[240px] ${
                stalled ? "border-red-300 bg-red-50/40" : "border-border"
              }`}
              data-testid={`analysis-card-${s.id}`}
            >
              <button
                type="button"
                onClick={() => onOpen(s.id)}
                className="text-left w-full px-3 py-2 pr-9 hover:bg-slate-50 rounded-md"
              >
                <div className="flex items-center gap-2 mb-1">
                  {pillFor(s)}
                  {s.subfolder && (
                    <span className="text-[11px] text-slate-500 font-mono truncate">
                      {s.subfolder}
                    </span>
                  )}
                  {s.batch_label && !(s.merged_batch_count && s.merged_batch_count > 1) && (
                    <span className="text-[10px] uppercase tracking-widest font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded-sm shrink-0">
                      {s.batch_label}
                    </span>
                  )}
                  {s.merged_batch_count && s.merged_batch_count > 1 && (
                    <span
                      className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm shrink-0"
                      title={`This card merges ${s.merged_batch_count} batches into one unified session view — every photo across every batch is included.`}
                      data-testid={`analysis-merged-batches-${s.id}`}
                    >
                      {s.merged_batch_count} batches merged
                    </span>
                  )}
                </div>
                <p className="text-sm font-semibold text-slate-800">
                  {progressText(s)}
                </p>
                {timingLine(s) && (
                  <p
                    className="text-[11px] text-slate-500 mt-0.5 tabular-nums"
                    data-testid={`analysis-timing-${s.id}`}
                  >
                    {timingLine(s)}
                  </p>
                )}
                {(s.preparing_previews_count ?? 0) > 0 && (
                  <p
                    className="text-[11px] text-violet-600 mt-0.5 font-medium"
                    data-testid={`analysis-preview-queue-${s.id}`}
                    title={
                      "Background pool is transcoding CR2/CR3 files into "
                      + "JPEG previews. The analyzer waits (never falls back "
                      + "to full-RAW decode). Pool concurrency: "
                      + (s.bg_preview_queue?.concurrency ?? 2)
                    }
                  >
                    {(() => {
                      const qTotal = s.bg_preview_queue?.total ?? 0;
                      const conc = s.bg_preview_queue?.concurrency ?? 2;
                      const running = Math.min(qTotal, conc);
                      const ahead = Math.max(0, qTotal - conc);
                      if (qTotal === 0) return null;
                      return ahead > 0
                        ? `Queue: ${running} transcoding · ${ahead} ahead of you`
                        : `Queue: ${running} transcoding`;
                    })()}
                  </p>
                )}
                {(s.error_count > 0 || s.skipped_count > 0 || s.error) && (
                  <p
                    className="text-[11px] text-red-600 mt-0.5 font-semibold"
                    data-testid={`analysis-errors-${s.id}`}
                    title={
                      s.error
                        || `${s.error_count} file error${s.error_count === 1 ? "" : "s"}`
                        + (s.errors && s.errors[0]
                          ? ` · ${s.errors[0].stage}: ${s.errors[0].filename}`
                          : "")
                    }
                  >
                    {s.error
                      ? `⚠ ${s.error}`
                      : `⚠ ${s.error_count || s.skipped_count} image${(s.error_count || s.skipped_count) === 1 ? "" : "s"} skipped`}
                  </p>
                )}
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {new Date(s.created_at).toLocaleString()}
                </p>
              </button>
              {/* Phase 55: manual "Retry errored files" button — visible
                  only when a completed/cancelled session still has one
                  or more files in the ``error`` state after auto-retry
                  rounds exhausted. Kicks off a small new session that
                  only re-processes those files. */}
              {s.error_count > 0 && ["complete", "ready", "cancelled", "failed"].includes(s.status) && (
                <div className="px-3 pb-1.5 -mt-1">
                  <button
                    type="button"
                    onClick={(e) => retryErrors(e, s)}
                    disabled={retryingId === s.id}
                    className="w-full inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md border border-red-200 bg-red-50 text-red-700 text-[11px] font-bold uppercase tracking-widest hover:bg-red-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    data-testid={`analysis-retry-errors-${s.id}`}
                    title={`Retry the ${s.error_count} file${s.error_count === 1 ? "" : "s"} that ended in error state`}
                  >
                    <RefreshCw className={`h-3 w-3 ${retryingId === s.id ? "animate-spin" : ""}`} />
                    {retryingId === s.id
                      ? "Starting retry…"
                      : `Retry ${s.error_count} errored file${s.error_count === 1 ? "" : "s"}`}
                  </button>
                </div>
              )}
              {/* Phase 66: manual "Resume" button for stalled sessions.
                  Unlike "Retry errored" it picks up ALL unfinished
                  files (pending / downloading / analyzing / error) —
                  the common stall pattern is workers hung mid-flight
                  with files never advancing to error state, so this
                  is the fastest path back to progress without losing
                  completed work. Visible while a session is still
                  active (downloading/analyzing/queued) OR stalled. */}
              {(isStalled(s)
                  || ["downloading", "analyzing", "queued"].includes(s.status))
                && (s.download_total ?? 0) > 0 && (
                <div className="px-3 pb-1.5 -mt-1">
                  <button
                    type="button"
                    onClick={(e) => resumeSession(e, s)}
                    disabled={resumingId === s.id}
                    className="w-full inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md border border-orange-300 bg-orange-50 text-orange-700 text-[11px] font-bold uppercase tracking-widest hover:bg-orange-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    data-testid={`analysis-resume-${s.id}`}
                    title="Cancel the stalled work and resume with the remaining files (completed work is preserved)"
                  >
                    <RotateCcw className={`h-3 w-3 ${resumingId === s.id ? "animate-spin" : ""}`} />
                    {resumingId === s.id ? "Resuming…" : "Resume unfinished"}
                  </button>
                </div>
              )}
              {/* Phase 51 / Phase 81: primary CTA for completed sessions.
                  - If AI grouping has NEVER run on this session yet,
                    render an inline grouping panel so the admin picks
                    a provider and stays on THIS page while grouping
                    runs in the background. The panel shows live
                    heartbeat progress and swaps into the "Review &
                    markup" button once grouping completes.
                  - If AI grouping is already done, render the
                    familiar Review & markup button (the primary
                    click target that resolved the "was the pill a
                    link?" ambiguity in Phase 51).
                  Both states are gated on the session being complete
                  and having photos to review. */}
              {(["complete", "ready"].includes(s.status)
                  && (s.photo_count ?? 0) > 0) && (() => {
                const gp = s.ai_grouping_progress || {};
                const alreadyGrouped = !!s.ai_grouped_at;
                const grouping = (
                  gp.status === "running"
                  || startingRegroupId === s.id
                );
                const failed = gp.status === "failed";
                const needsGrouping = (
                  !alreadyGrouped
                  && !grouping
                  && (s.photo_count ?? 0) > 1
                );

                // Reset the "starting" latch once the server confirms
                // running (or definitively fails/completes).
                if (
                  startingRegroupId === s.id
                  && (gp.status === "running" || gp.status === "failed" || gp.status === "complete")
                ) {
                  // side-effect deferred: React setState inside render
                  // is unsafe. Use a microtask so it fires after commit.
                  Promise.resolve().then(() => setStartingRegroupId(null));
                }

                if (grouping) {
                  const cur = gp.current_window || 0;
                  const total = gp.total_windows || 0;
                  const pct = total ? Math.min(100, Math.round((cur / total) * 100)) : null;
                  return (
                    <div
                      className="px-3 pb-2 -mt-1 space-y-2"
                      data-testid={`analysis-grouping-inline-${s.id}`}
                    >
                      <div className="border border-primary/40 bg-primary/5 rounded-md p-2.5 space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <Loader2 className="h-3.5 w-3.5 text-primary animate-spin shrink-0" />
                            <span className="text-[11px] font-bold uppercase tracking-widest text-primary truncate">
                              Grouping · {gp.provider || "openai"}
                            </span>
                          </div>
                          {pct != null && (
                            <span className="text-[11px] font-mono text-primary tabular-nums">
                              {pct}%
                            </span>
                          )}
                        </div>
                        <div className="h-1.5 bg-white rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all"
                            style={{ width: `${pct ?? 15}%` }}
                          />
                        </div>
                        <p className="text-[10px] text-slate-500">
                          {gp.phase
                            ? gp.phase
                            : startingRegroupId === s.id
                              ? "Starting AI grouping…"
                              : "Runs in the background — safe to leave this page"}
                          {total > 0 && ` · window ${cur}/${total}`}
                        </p>
                      </div>
                    </div>
                  );
                }

                if (needsGrouping) {
                  return (
                    <div
                      className="px-3 pb-2 -mt-1 space-y-1.5"
                      data-testid={`analysis-group-picker-${s.id}`}
                    >
                      <div className="border border-slate-200 rounded-md p-2.5 space-y-2">
                        <div>
                          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                            Ready to group
                          </p>
                          <p className="text-[11px] text-slate-600">
                            Pick a vision AI to identify bursts. Runs in the background — you can keep uploading.
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={(e) => startAiRegroup(e, s, "openai")}
                            className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-md bg-primary text-white text-[11px] font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors disabled:opacity-60"
                            data-testid={`analysis-group-openai-${s.id}`}
                          >
                            <Wand2 className="h-3 w-3" />
                            OpenAI
                          </button>
                          <button
                            type="button"
                            onClick={(e) => startAiRegroup(e, s, "gemini")}
                            className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-md border border-violet-300 bg-violet-50 text-violet-800 text-[11px] font-bold uppercase tracking-widest hover:bg-violet-100 transition-colors disabled:opacity-60"
                            data-testid={`analysis-group-gemini-${s.id}`}
                          >
                            <Wand2 className="h-3 w-3" />
                            Gemini
                          </button>
                        </div>
                        {failed && gp.error && (
                          <p
                            className="text-[10px] text-red-600 font-medium"
                            data-testid={`analysis-group-error-${s.id}`}
                          >
                            Last attempt failed: {gp.error}. Pick a provider to retry.
                          </p>
                        )}
                      </div>
                    </div>
                  );
                }

                // Fully grouped — render the primary Review & Markup CTA.
                return (
                  <div className="px-3 pb-2 -mt-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onOpen(s.id);
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md bg-primary text-white text-xs font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors"
                      data-testid={`analysis-open-${s.id}`}
                      title="Open the analyzer and review the grouped photos"
                    >
                      <Wand2 className="h-3.5 w-3.5" />
                      Review &amp; markup
                      <span className="text-white/70 text-[10px]">→</span>
                    </button>
                  </div>
                );
              })()}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setExpandedId((cur) => (cur === s.id ? null : s.id));
                }}
                className="absolute bottom-1.5 right-8 text-[10px] font-semibold text-slate-500 hover:text-primary uppercase tracking-wider"
                data-testid={`analysis-toggle-details-${s.id}`}
              >
                {expandedId === s.id ? "Hide details" : "Show details"}
              </button>
              {expandedId === s.id && (
                <SessionDetails
                  sessionId={s.id}
                  isActive={s.status === "downloading" || s.status === "analyzing"}
                />
              )}
              {canCancel && (
                <button
                  type="button"
                  onClick={(e) => cancel(e, s)}
                  disabled={cancellingId === s.id}
                  title={cancelTitle(s)}
                  className="absolute top-1.5 right-1.5 p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-wait transition-colors"
                  data-testid={`analysis-cancel-${s.id}`}
                >
                  <XCircle className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ── AssigneeControl ────────────────────────────────────────────────
 * Admin-only header widget that shows the current assignee and lets
 * the admin reassign the job to another user via a dropdown. Not
 * rendered for standard users — they see just the email in the header.
 */
function AssigneeControl({ jobId, currentUserId, currentEmail, onChanged }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/users`).then((r) => setUsers(r.data || []))
      .catch(() => toast.error("Could not load users"));
  }, [open]);

  const reassign = async (newId, newEmail) => {
    if (newId === currentUserId) { setOpen(false); return; }
    if (!window.confirm(`Reassign this job to ${newEmail}?`)) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/jobs/${jobId}`, { assigned_user_id: newId });
      toast.success(`Reassigned to ${newEmail}`);
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative" data-testid="assignee-control">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md px-2 py-1 font-medium transition-colors"
        title="Reassign this job to another user"
        data-testid="assignee-current"
      >
        <UserCog className="h-3.5 w-3.5 text-slate-400" />
      <span
        className="max-w-[9rem] truncate"
        title={`Assignee: ${currentEmail || "unassigned"} — click to reassign`}
      >
        {currentEmail || "unassigned"}
      </span>
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-md shadow-lg z-40 max-h-80 overflow-y-auto"
          data-testid="assignee-dropdown"
        >
          <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-medium">
            <Users className="h-3 w-3" /> Reassign to
          </div>
          {users.length === 0 && (
            <p className="px-3 py-3 text-xs text-slate-400">Loading…</p>
          )}
          {users.map((u) => {
            const isCurrent = u.id === currentUserId;
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => reassign(u.id, u.email)}
                disabled={saving}
                className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between gap-2 hover:bg-slate-50 ${
                  isCurrent ? "bg-blue-50 text-blue-700" : "text-slate-700"
                }`}
                data-testid={`assignee-option-${u.id}`}
              >
                <span className="truncate min-w-0">
                  <span className="font-medium">{u.name || u.email}</span>
                  <span className="block text-[10px] text-slate-500 truncate">{u.email}</span>
                </span>
                {isCurrent && <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ── JobMarkupPanel ─────────────────────────────────────────────────
 * Renders every photo in the job that has markups drawn on it, so
 * the editor can see EXACTLY which areas need attention before
 * downloading the source files. Read-only for standard users; admins
 * see a small "Edit in analyzer" link on each card.
 */
function JobMarkupPanel({ jobId, isAdmin, onOpenAnalyzer }) {
  const [markups, setMarkups] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/jobs/${jobId}/markups`);
      setMarkups(r.data?.markups || []);
    } catch (e) {
      /* silent — panel just shows empty state */
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <section className="rounded-md border border-border bg-white p-4">
        <p className="text-xs text-slate-400">Loading markup instructions…</p>
      </section>
    );
  }

  if (markups.length === 0) {
    return null; // don't clutter the page when there's nothing to edit
  }

  const totalBoxes = markups.reduce((n, m) => n + (m.boxes || []).length, 0);

  return (
    <section
      className="rounded-md border border-accent/40 bg-orange-50/40 overflow-hidden"
      data-testid="job-markup-panel"
    >
      <div className="px-4 py-3 border-b border-accent/30 bg-orange-100/50 flex items-center gap-2">
        <FileText className="h-4 w-4 text-accent" />
        <h3 className="font-heading font-bold text-sm tracking-tight text-slate-900">
          Markup instructions
        </h3>
        <span className="text-[11px] text-slate-600 font-medium">
          <span className="font-mono text-slate-900">{markups.length}</span> photo{markups.length === 1 ? "" : "s"} ·
          <span className="font-mono text-slate-900 ml-1">{totalBoxes}</span> area{totalBoxes === 1 ? "" : "s"}
        </span>
        <span className="text-[10px] text-slate-500 ml-auto">
          {isAdmin
            ? "Click Open analyzer above to add or edit these"
            : "Read-only — download originals and edit per the instructions below"}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
        {markups.map((m) => (
          <MarkupPhotoCard
            key={m.filename}
            jobId={jobId}
            filename={m.filename}
            cdnUrl={m.cdn_url}
            boxes={m.boxes || []}
            isAdmin={isAdmin}
            /* Phase 90: pass the markup's OWN session_id so the deep
               link goes straight to the session that actually owns
               this photo (not the first-usable session on the job). */
            sessionId={m.session_id}
            onOpenAnalyzer={onOpenAnalyzer}
          />
        ))}
      </div>
    </section>
  );
}


// Phase 87: editor's Rework Queue. Renders a compact panel listing
// every photo across the job whose markups still contain at least one
// ``review_status: "rework"`` box. Editors see the batch of work they
// still owe the admin — grouped by ``group_id`` so a per-group batch
// stays coherent — and can click straight through to the analyzer at
// the exact photo/group in question.
function ReworkQueuePanel({ jobId, onOpenAnalyzer }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/jobs/${jobId}/rework-queue`);
      setData(r.data);
    } catch (_e) {
      /* silent — panel hides on error */
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data || data.total_photos === 0) return null;

  // Bucket items by group_id so the editor sees each batch together.
  const buckets = new Map();
  for (const it of data.items) {
    const key = it.group_id || "__ungrouped__";
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(it);
  }
  const groupKeys = [...buckets.keys()];

  return (
    <section
      className="rounded-md border border-red-300 bg-red-50/50 overflow-hidden"
      data-testid="rework-queue-panel"
    >
      <div className="px-4 py-3 border-b border-red-200 bg-red-100/60 flex items-center gap-2">
        <RotateCcw className="h-4 w-4 text-red-600" />
        <h3 className="font-heading font-bold text-sm tracking-tight text-slate-900">
          Rework queue
        </h3>
        <span className="text-[11px] text-slate-700 font-medium">
          <span className="font-mono text-red-700">{data.total_photos}</span> photo{data.total_photos === 1 ? "" : "s"} ·
          <span className="font-mono text-red-700 ml-1">{data.total_boxes}</span> area{data.total_boxes === 1 ? "" : "s"}
          <span className="ml-1 text-slate-500">flagged for rework</span>
        </span>
        <span className="text-[10px] text-slate-500 ml-auto">
          Fix each area, upload the edit, and it will drop off this list.
        </span>
      </div>
      <div className="p-4 space-y-4" data-testid="rework-queue-list">
        {groupKeys.map((gid) => (
          <div key={gid} className="rounded-sm border border-red-200 bg-white/60 p-3">
            <p className="text-[10px] uppercase tracking-widest font-bold text-red-700 mb-2">
              Group {gid === "__ungrouped__" ? "(unassigned)" : gid}
              <span className="ml-2 text-slate-500 font-mono">
                {buckets.get(gid).length} photo{buckets.get(gid).length === 1 ? "" : "s"}
              </span>
            </p>
            <div className="space-y-2">
              {buckets.get(gid).map((it) => (
                <div
                  key={it.filename}
                  className="rounded-sm border border-slate-200 bg-white p-2.5"
                  data-testid={`rework-item-${it.filename}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="rounded-full bg-red-500 text-white px-1.5 h-5 text-[10px] font-bold inline-flex items-center">
                        ↻ {it.rework_count}
                      </span>
                      <p
                        className="font-mono text-[12px] text-slate-800 truncate"
                        title={it.filename}
                      >
                        {it.filename}
                      </p>
                      {it.rank && (
                        <span className="text-[10px] text-slate-500 shrink-0">
                          rank {it.rank}
                        </span>
                      )}
                    </div>
                    {onOpenAnalyzer && it.session_id && (
                      <button
                        type="button"
                        onClick={() => onOpenAnalyzer(it.filename, it.session_id)}
                        className="text-[10px] font-bold uppercase tracking-widest text-red-700 hover:text-red-800 hover:underline shrink-0"
                        data-testid={`rework-open-${it.filename}`}
                      >
                        Open →
                      </button>
                    )}
                  </div>
                  <ul className="ml-6 space-y-1 text-[11px] text-slate-700">
                    {it.rework_notes.map((n, i) => (
                      <li key={i} className="leading-tight">
                        <span className="font-mono text-slate-500 mr-1">#{i + 1}</span>
                        {n.label && (
                          <span className="font-semibold text-slate-900">
                            {n.label}
                            {(n.review_note || n.note) ? ": " : ""}
                          </span>
                        )}
                        <span>
                          {n.review_note || n.note || <span className="italic text-slate-400">(no note)</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}


// File extensions the browser can render natively — for these we serve
// straight from Cloudflare's CDN (r2.dev public URL) and skip the
// backend transcode+cache path entirely. RAW formats and HEIC fall
// back to /preview which still transcodes on the server.
const NATIVE_BROWSER_EXT = new Set([
  "jpg", "jpeg", "png", "webp", "gif", "bmp",
]);

function pickImageUrl({ jobId, filename, cdnUrl, folder = "uploaded" }) {
  const dot = filename.lastIndexOf(".");
  const ext = dot >= 0 ? filename.slice(dot + 1).toLowerCase() : "";
  if (cdnUrl && NATIVE_BROWSER_EXT.has(ext)) return cdnUrl;
  return `${API}/jobs/${jobId}/files/${encodeURIComponent(filename)}/preview?folder=${folder}`;
}


function MarkupPhotoCard({ jobId, filename, cdnUrl, boxes, isAdmin, sessionId, onOpenAnalyzer }) {
  const [imgErr, setImgErr] = useState(false);
  const previewUrl = pickImageUrl({ jobId, filename, cdnUrl });
  const manualBoxes = boxes.filter((b) => b.source !== "auto");
  const autoBoxes = boxes.filter((b) => b.source === "auto");

  return (
    <div
      className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm"
      data-testid={`markup-card-${filename}`}
    >
      <div className="relative bg-slate-900" style={{ paddingTop: "66.66%" }}>
        {!imgErr ? (
          <img
            src={previewUrl}
            alt={filename}
            className="absolute inset-0 w-full h-full object-contain select-none"
            draggable={false}
            onError={() => setImgErr(true)}
            data-testid={`markup-card-image-${filename}`}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs">
            Preview unavailable
          </div>
        )}
        {/* Rectangles rendered over the preview at the correct positions */}
        <div className="absolute inset-0 pointer-events-none">
          {boxes.map((b, i) => (
            <div
              key={i}
              className={`absolute border-2 ${
                b.source === "auto"
                  ? "border-amber-500 bg-amber-500/10"
                  : "border-accent bg-accent/10"
              }`}
              style={{
                left: `${(b.x || 0) * 100}%`,
                top: `${(b.y || 0) * 100}%`,
                width: `${(b.w || 0) * 100}%`,
                height: `${(b.h || 0) * 100}%`,
              }}
              data-testid={`markup-card-box-${filename}-${i}`}
            >
              <span
                className={`absolute -top-4 left-0 text-[9px] font-bold uppercase tracking-widest px-1 py-0.5 rounded-sm whitespace-nowrap ${
                  b.source === "auto" ? "bg-amber-500 text-black" : "bg-accent text-white"
                }`}
              >
                #{i + 1}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <FileText className="h-3 w-3 text-slate-400 shrink-0" />
          <span className="font-mono text-[11px] text-slate-900 truncate flex-1">
            {filename}
          </span>
          <span className="text-[10px] text-slate-500 font-medium whitespace-nowrap">
            {manualBoxes.length} manual
            {autoBoxes.length > 0 && (
              <span className="text-amber-600 ml-1">
                · {autoBoxes.length} auto
              </span>
            )}
          </span>
        </div>
        <ol className="space-y-1.5 text-[11px] text-slate-700 pl-1">
          {boxes.map((b, i) => {
            const st = b.review_status || "pending";
            const badgeCls = st === "approved"
              ? "bg-emerald-500 text-black"
              : st === "rework"
              ? "bg-red-500 text-white"
              : b.source === "auto"
              ? "bg-amber-500 text-black"
              : "bg-accent text-white";
            return (
              <li
                key={i}
                className={`flex items-start gap-2 ${
                  st === "rework" ? "bg-red-50 rounded-sm p-1 -mx-1" : ""
                }`}
                data-testid={`markup-card-instruction-${filename}-${i}`}
              >
                <span
                  className={`text-[9px] font-mono font-bold px-1 py-0.5 rounded-sm mt-0.5 shrink-0 ${badgeCls}`}
                >
                  #{i + 1}
                  {st === "approved" && " ✓"}
                  {st === "rework" && " ↻"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium ${
                    st === "approved" ? "text-emerald-700 line-through" : "text-slate-900"
                  }`}>
                    {b.label || `Area ${i + 1}`}
                    {st === "rework" && (
                      <span className="ml-2 text-[9px] font-bold uppercase tracking-widest bg-red-500 text-white px-1 py-0.5 rounded-sm">
                        NOT PASSED · Rework
                      </span>
                    )}
                    {st === "approved" && (
                      <span className="ml-2 text-[9px] font-bold uppercase tracking-widest bg-emerald-500 text-black px-1 py-0.5 rounded-sm">
                        Approved
                      </span>
                    )}
                  </div>
                  {b.note && (
                    <div className="text-slate-600 mt-0.5 whitespace-pre-wrap break-words">
                      {b.note}
                    </div>
                  )}
                  {b.review_note && (
                    <div className={`text-[10px] mt-1 pl-2 border-l-2 ${
                      st === "rework"
                        ? "border-red-500 text-red-700"
                        : "border-emerald-500 text-emerald-700"
                    }`}>
                      <span className="font-bold uppercase tracking-widest text-[9px] mr-1">
                        {st === "rework" ? "Admin (Rework):" : "Admin:"}
                      </span>
                      {b.review_note}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
        {isAdmin && onOpenAnalyzer && (
          <button
            type="button"
            onClick={() => onOpenAnalyzer(filename, sessionId)}
            className="text-[10px] text-primary hover:underline font-medium"
            data-testid={`markup-card-open-${filename}`}
          >
            Edit in analyzer →
          </button>
        )}
      </div>
    </div>
  );
}




export default function JobDetailPage() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzerSessionId, setAnalyzerSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}`);
      setJob(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      nav("/");
    } finally {
      setLoading(false);
    }
  }, [jobId, nav]);

  const loadAnalyzerSession = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}/analyze/latest`);
      setAnalyzerSessionId(data?.session?.id || null);
    } catch {
      /* silent — analyzer session is optional */
    }
  }, [jobId]);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}/analyze/sessions`);
      setSessions(data?.sessions || []);
    } catch {
      /* silent */
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAnalyzerSession(); }, [loadAnalyzerSession]);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Belt-and-braces: if the /analyze/latest endpoint returned no session
  // (older data, projection mismatch, transient error) BUT the sessions
  // list contains a usable one, adopt it. This is what fixes the
  // "floating bar keeps saying 'Analyze photos' even though I already
  // have a complete session" bug the user hit after Phase 50 —
  // ``analyzerSessionId`` stays null and the button offers to start yet
  // another (redundant) analyze run.
  //
  // Phase 88: prefer the MERGED / AI-grouped session over any raw batch
  // child. Without this, jobs that were analyzed in multiple batches
  // (e.g. "batch 1/2", "batch 2/2") land the user on the FIRST batch —
  // which has no ai_grouped_at set — so the Analyzer opens on the
  // "Group with AI" pipeline card instead of the real group grid. The
  // parent (merged) session is the only one whose group grid is
  // useful for the "Edit in Analyzer" deep-link.
  useEffect(() => {
    if (analyzerSessionId) return;
    if (!sessions || sessions.length === 0) return;
    const usableStatuses = ["complete", "ready", "downloading", "analyzing"];
    // Priority 1: any session with ai_grouped_at set (the merged parent
    // when batches were merged, or a single-batch session that has been
    // AI-grouped).
    const grouped = sessions.find(
      (s) => usableStatuses.includes(s.status) && s.ai_grouped_at,
    );
    // Priority 2: a session that is NOT a child of another (i.e. no
    // parent_session_id) — this is the merged parent, or an
    // un-merged single session.
    const parent = sessions.find(
      (s) => usableStatuses.includes(s.status) && !s.parent_session_id,
    );
    // Priority 3: fall back to anything usable so we never regress the
    // original "session_id stays null" bug this effect was born to fix.
    const usable = grouped || parent || sessions.find(
      (s) => usableStatuses.includes(s.status),
    );
    if (usable?.id) setAnalyzerSessionId(usable.id);
  }, [sessions, analyzerSessionId]);

  // Poll active analyzer sessions so progress updates in place while
  // the admin stays on this page. Phase 81: also poll while any
  // session's AI grouping is RUNNING so the inline group panel
  // heartbeats live. We deliberately do NOT poll on "needs-grouping"
  // sessions — that would keep the interval alive forever on any job
  // with an ungrouped complete session even when the admin never
  // clicks a provider. Instead, ``startAiRegroup`` calls
  // ``onCancelled`` which triggers an immediate ``loadSessions``,
  // so the poll starts up as soon as the running state arrives.
  useEffect(() => {
    const hasActive = sessions.some((s) => {
      if (s.status === "downloading" || s.status === "analyzing") return true;
      if (s.ai_grouping_progress?.status === "running") return true;
      return false;
    });
    if (!hasActive) return;
    const t = setInterval(loadSessions, 3000);
    return () => clearInterval(t);
  }, [sessions, loadSessions]);

  const startAnalyze = async (subfolder = null) => {
    setAnalyzing(true);
    try {
      const url = subfolder
        ? `${API}/jobs/${jobId}/analyze?subfolder=${encodeURIComponent(subfolder)}`
        : `${API}/jobs/${jobId}/analyze`;
      const { data } = await axios.post(url);
      // Phase 91: check the process-wide queue depth so the toast can
      // tell the admin whether their analysis starts NOW or has to
      // wait behind another running/queued session. Best-effort — a
      // queue-lookup failure just falls back to the original toast.
      let queueSuffix = "";
      try {
        const { data: q } = await axios.get(`${API}/analyze/queue`);
        // Number of sessions that will run BEFORE this admin's newly
        // enqueued batch. Everything in ``running`` runs first, plus
        // any queued entries created before the ones we just added.
        const ourSessionIds = new Set(
          (data.sessions || []).map((s) => s.session_id).filter(Boolean),
        );
        const ahead = [
          ...(q.running || []),
          ...((q.queued || []).filter((s) => !ourSessionIds.has(s.id))),
        ].length;
        if (ahead > 0) {
          queueSuffix = ` (queued behind ${ahead} other session${ahead === 1 ? "" : "s"} — runs automatically)`;
        }
      } catch { /* ignore queue lookup failure */ }
      toast.success(
        (subfolder
          ? `Analyzing ${data.photo_count} photos from “${subfolder}”`
          : `Analyzing ${data.photo_count} photos`)
        + queueSuffix,
        {
          duration: 5000,
          action: {
            label: "Open analyzer",
            onClick: () => nav(`/session/${data.session_id}?job=${jobId}`),
          },
        },
      );
      // Refresh recent-sessions list so the new one shows up.
      loadSessions();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const openAnalyzer = () => {
    if (analyzerSessionId) nav(`/session/${analyzerSessionId}?job=${jobId}`);
  };

  const commit = async () => {
    if (!window.confirm("Mark this job Ready for review? The admin will be notified.")) return;
    try {
      await axios.post(`${API}/jobs/${jobId}/commit`);
      toast.success("Committed. Admin will see it in their review queue.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const setStatus = async (newStatus, confirmMsg, successMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      await axios.patch(`${API}/jobs/${jobId}`, { status: newStatus });
      toast.success(successMsg);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  // Trigger a streaming ZIP download from the /files/zip endpoint.
  // ``folder`` accepts ``uploaded`` | ``edited`` | ``all``. The browser
  // handles the actual download so the page stays responsive.
  const downloadFolderZip = async (folder) => {
    try {
      await axios.get(`${API}/auth/me`);   // refresh cookie if needed
      const url = `${API}/jobs/${jobId}/files/zip?folder=${folder}`;
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      a.download = `${job?.name || "job"}-${folder}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.info(
        folder === "all"
          ? "Bundling every file for this job — check your browser's downloads."
          : `Bundling ${folder} — check your browser's downloads.`,
        { duration: 8000 },
      );
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    }
  };

  if (loading || !job) {
    return (
      <div className="min-h-screen bg-background p-10">
        <p className="text-slate-500 font-medium">Loading…</p>
      </div>
    );
  }

  const isAdmin = user?.role === "admin";
  const isAssignee = user?.id && job.assigned_user_id === user.id;
  const canCommit = (isAdmin || isAssignee) && job.status !== "closed";

  return (
    <div className="min-h-screen bg-background text-slate-900">
      <AppTopBar
        backTo="/"
        eyebrow="Job"
        title={job.name}
        titleAdornment={
          <div className="flex items-center gap-3">
            <StatusPill status={job.status} />
            {isAdmin && (
              <AssigneeControl
                jobId={jobId}
                currentUserId={job.assigned_user_id}
                currentEmail={job.assigned_user_email}
                onChanged={load}
              />
            )}
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            {/* Phase 57: "Open analyzer" removed — the session cards
                below now carry a prominent "Review & markup" CTA per
                completed session, so a top-bar shortcut for the LATEST
                one only adds noise. Kept the "Analyze photos" button
                (only when there's no completed session yet). */}
            {isAdmin && !analyzerSessionId && (
              <Button
                onClick={startAnalyze}
                disabled={analyzing || (job.uploaded_count ?? 0) === 0}
                variant="outline"
                className="border-primary text-primary hover:bg-primary/5 rounded-md h-9 font-semibold"
                data-testid="btn-analyze-photos"
                title="Run the Photo Analyzer on the uploaded folder"
              >
                <Wand2 className="h-4 w-4 mr-1.5" />
                {analyzing ? "Starting…" : "Analyze photos"}
              </Button>
            )}
            {isAdmin && job.status === "ready_for_review" && (
              <Button
                onClick={() => nav(`/jobs/${jobId}/review`)}
                variant="outline"
                className="border-accent text-accent hover:bg-accent/10 rounded-md h-9 font-semibold"
                data-testid="btn-open-review"
              >
                <Eye className="h-4 w-4 mr-1.5" />Open 3-way review
              </Button>
            )}
            {isAdmin && job.status === "ready_for_review" && (
              <>
                <Button
                  onClick={() => setStatus(
                    "in_progress",
                    "Send this job back to the editor for rework?",
                    "Reopened for rework",
                  )}
                  variant="outline"
                  className="border-border text-slate-700 hover:bg-slate-50 rounded-md h-9 font-semibold"
                  data-testid="btn-reopen-rework"
                >
                  <RotateCcw className="h-4 w-4 mr-1.5" />Send back for rework
                </Button>
                <Button
                  onClick={() => setStatus(
                    "closed",
                    "Close this job? The editor will no longer be able to make changes.",
                    "Job closed",
                  )}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md h-9 font-semibold shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow]"
                  data-testid="btn-close-job"
                >
                  <XCircle className="h-4 w-4 mr-1.5" />Approve &amp; close
                </Button>
              </>
            )}
            {isAdmin && job.status === "closed" && (
              <>
                <Button
                  onClick={() => setStatus(
                    "in_progress",
                    "Reopen this closed job?",
                    "Job reopened",
                  )}
                  variant="outline"
                  className="border-border text-slate-700 hover:bg-slate-50 rounded-md h-9 font-semibold"
                  data-testid="btn-reopen-closed"
                >
                  <RotateCcw className="h-4 w-4 mr-1.5" />Reopen
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 rounded-md h-9 font-semibold"
                      data-testid="btn-download-closed"
                    >
                      <Download className="h-4 w-4 mr-1.5" />Download
                      <ChevronDown className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64"
                                        data-testid="download-closed-menu">
                    <DropdownMenuItem
                      onClick={() => downloadFolderZip("edited")}
                      data-testid="download-edited-only"
                    >
                      <Download className="h-4 w-4" />
                      Edited files only
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => downloadFolderZip("all")}
                      data-testid="download-everything"
                    >
                      <Package className="h-4 w-4" />
                      Everything (uploaded + edited + markups)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            {canCommit && (
              <Button
                onClick={commit}
                disabled={job.status === "ready_for_review"}
                className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-md h-9 font-semibold shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow]"
                data-testid="btn-commit-job"
                title={job.status === "ready_for_review" ? "Already committed — waiting on admin review" : "Mark this job as ready for admin review"}
              >
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                {job.status === "ready_for_review" ? "Awaiting review" : "Submit for review"}
              </Button>
            )}
          </div>
        }
      />

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-8 space-y-6">
        {job.description && (
          <div className="border-l-4 border-primary bg-blue-50/60 px-4 py-3 rounded-r-md">
            <p className="text-sm text-slate-700 font-medium">{job.description}</p>
          </div>
        )}

        {job.status === "ready_for_review" && (
          <div
            className="border-l-4 border-accent bg-orange-50/70 px-4 py-3 rounded-r-md flex items-start gap-3"
            data-testid="review-banner"
          >
            <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-bold text-slate-900">Awaiting admin review</p>
              <p className="text-slate-700 mt-0.5">
                {isAdmin
                  ? "Review the deliverables below. Send back for rework or approve & close."
                  : "The admin has been notified. Files remain editable if you need to push more changes before review."}
              </p>
            </div>
          </div>
        )}

        {job.status === "closed" && (
          <div
            className="border-l-4 border-slate-400 bg-slate-100 px-4 py-3 rounded-r-md flex items-start gap-3"
            data-testid="closed-banner"
          >
            <Lock className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-bold text-slate-900">Job closed</p>
              <p className="text-slate-700 mt-0.5">
                {isAdmin
                  ? "Standard users can only view + download. Reopen to unlock edits."
                  : "This job has been closed by the admin. Downloads only."}
              </p>
            </div>
          </div>
        )}

        {/* Recent analyses — always show when there's at least one so
            the admin can jump into any of them and see live status
            (downloading / analyzing / ready). Multiple sessions are
            common now that Analyze is folder-scoped. */}
        {sessions.length > 0 && (
          <RecentAnalyses
            sessions={sessions}
            onOpen={(sid) => nav(`/session/${sid}?job=${jobId}`)}
            jobId={jobId}
            isAdmin={isAdmin}
            onCancelled={() => { loadSessions(); loadAnalyzerSession(); }}
          />
        )}

        {/* Phase 87: rework queue — shown to both editor & admin when
            ANY markup in this job is currently flagged for rework. The
            editor uses it as their batch to-do list; the admin gets a
            handy summary of what's still outstanding. */}
        {sessions.length > 0 && (
          <ReworkQueuePanel
            jobId={job.id}
            onOpenAnalyzer={
              analyzerSessionId
                ? (filename, sid) => nav(
                  `/session/${sid || analyzerSessionId}?job=${jobId}` +
                  (filename ? `&photo=${encodeURIComponent(filename)}` : ""),
                )
                : null
            }
          />
        )}

        {/* Markup instructions — visible to editor & admin. Only rendered
            when there are markups drawn on any photo in this job.
            Phase 58: also gated on ``sessions.length > 0`` so the panel
            never surfaces stale markups pointing at deleted sessions
            (ghost data). The backend now cascade-deletes on last-session
            removal, this is an extra belt-and-braces guard for legacy
            data still in prod. */}
        {sessions.length > 0 && (
          <JobMarkupPanel
            jobId={job.id}
            isAdmin={isAdmin}
            onOpenAnalyzer={(filename, sid) => {
              // Phase 90: prefer the markup's OWN session_id — falls
              // back to the job's auto-picked analyzerSessionId so
              // legacy markups without a resolved session still work.
              const target = sid || analyzerSessionId;
              if (!target) return;
              nav(
                `/session/${target}?job=${jobId}` +
                (filename ? `&photo=${encodeURIComponent(filename)}` : ""),
              );
            }}
          />
        )}

        {/* Asymmetric 5 / 7 split — source is narrower, deliverables wider */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-5">
            <FolderPanel
              jobId={job.id}
              folder="uploaded"
              jobName={job.name}
              canUpload={isAdmin || (job.status !== "closed")}
              canDelete={isAdmin}
              tone="primary"
              onChanged={load}
              onAnalyzeFolder={isAdmin ? startAnalyze : null}
              testidPrefix="uploaded"
            />
          </div>
          <div className="lg:col-span-7">
            <FolderPanel
              jobId={job.id}
              folder="edited"
              jobName={job.name}
              canUpload={isAdmin || (job.status !== "closed")}
              canDelete={isAdmin || (job.status !== "closed")}
              tone="accent"
              onChanged={load}
              testidPrefix="edited"
            />
          </div>
        </div>
      </main>
    </div>
  );
}
